// The chat's input: several lines, Enter sends, Shift+Enter is a new line, and Stop while a turn runs.

import { forwardRef, useLayoutEffect, useRef } from 'react';
import type { ForwardedRef } from 'react';
import { sendsOnKey } from '../lib/chatFeed.ts';

// The tallest the box grows before it scrolls.
const MAX_ROWS = 8;
const LINE_HEIGHT_PX = 20;

export const ChatComposer = forwardRef(function ChatComposer({
  draft, placeholder, label, running, disabled, disabledReason, onDraftChange, onSend, onStop,
}: {
  draft: string;
  placeholder: string;
  label: string;
  running: boolean;
  disabled: boolean;
  disabledReason?: string;
  onDraftChange: (draft: string) => void;
  onSend: () => void;
  onStop: () => void;
}, forwardedRef: ForwardedRef<HTMLTextAreaElement>) {
  const localRef = useRef<HTMLTextAreaElement | null>(null);

  // Grow with the text, up to MAX_ROWS.
  useLayoutEffect(() => {
    const box = localRef.current;
    if (!box) return;
    box.style.height = 'auto';
    box.style.height = `${Math.min(box.scrollHeight, MAX_ROWS * LINE_HEIGHT_PX + 20)}px`;
  }, [draft]);

  function attach(element: HTMLTextAreaElement | null) {
    localRef.current = element;
    if (typeof forwardedRef === 'function') forwardedRef(element);
    else if (forwardedRef) forwardedRef.current = element;
  }

  return (
    <div className="actionrow chatcomposer">
      <textarea
        ref={attach}
        rows={1}
        placeholder={placeholder}
        aria-label={label}
        value={draft}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        onChange={event => onDraftChange(event.target.value)}
        onKeyDown={event => {
          if (sendsOnKey(event.key, event.shiftKey, event.nativeEvent.isComposing)) {
            event.preventDefault();
            onSend();
          }
        }}
      />
      {running ? (
        <button type="button" className="btn chatstop" onClick={onStop} aria-label="Stop Scout's answer">
          Stop
        </button>
      ) : null}
      <button type="button" className="btn" disabled={disabled || !draft.trim()} onClick={onSend}>
        Send
      </button>
    </div>
  );
});
